import memoize from 'memoize-one';
import { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';

import { onlineCourseQuestionAsked } from 'my-core/gtm-events';
import { useAddMessage, useCurrentUser, useReadViewing, useStandardApiRequest } from 'my-core/hooks';
import { useCurrentUserSubscription } from 'my-core/subscription-utils';

import QuestionForm from './OnlineCourseQuestionForm';

import { createOnlineCourseQuestion } from 'my-actions/OnlineCourseQuestionActions';

let formKeyCounter = 0;

export default function OnlineCourseQuestionFormCtnr({
  courseId,
  defaultQuestionParams,
  enableTour,
  eventTrackingProperties,
  onlineCourse,
  onlineCourseId: onlineCourseIdProp,
  onPurchase,
  questionableId,
  questionableType,
}) {
  const onlineCourseId = onlineCourse?.id || onlineCourseIdProp;
  const currentUser = useCurrentUser();
  const subscription = useCurrentUserSubscription();
  const addMessage = useAddMessage();
  const readViewing = useReadViewing();
  const [formKey, setFormKey] = useState(formKeyCounter++);

  const onlineCourseUser = useSelector(state =>
    getOnlineCourseUser(state.onlineCourseUsers.items, onlineCourseId, currentUser.id),
  );
  const userCourse = useSelector(state =>
    getUserCourse(state.userCourses.items, onlineCourse?.course_id || courseId, currentUser.id),
  );

  const { performRequest: performCreate /*requestStatus: createStatus */ } = useStandardApiRequest({
    actionCreator: createOnlineCourseQuestion,
    errorMessage: true,
    onSuccess: ({ ai_tutor_replying: aiTutorReplying }) => {
      // Otherwise the AI Tutor's answer streams in right below, so there is nothing to wait to be told about.
      if (!aiTutorReplying) {
        addMessage({
          content: 'Thanks for your question.\nYou will be notified when your Wizeprep Prof responds.',
          type: 'success',
        });
      }
      setFormKey(formKeyCounter++);
      onlineCourseQuestionAsked(eventTrackingProperties);
    },
  });
  const saveQuestion = useCallback(
    params => {
      performCreate({
        ...defaultQuestionParams,
        ...params,
        online_course_id: onlineCourseId,
        questionable_id: questionableId,
        questionable_type: questionableType,
        viewing: readViewing?.(),
      });
    },
    [defaultQuestionParams, onlineCourseId, performCreate, questionableId, questionableType, readViewing],
  );

  return (
    <QuestionForm
      key={formKey}
      currentUser={currentUser}
      enableTour={enableTour}
      onlineCourse={onlineCourse}
      onlineCourseUser={onlineCourseUser}
      onPurchase={onPurchase}
      onSave={saveQuestion}
      subscription={subscription}
      userCourse={userCourse}
    />
  );
}

const getOnlineCourseUser = memoize((ocus, ocId, uid) =>
  Object.values(ocus).find(ocu => ocu.online_course_id === ocId && ocu.user_id === uid),
);

const getUserCourse = memoize((ucs, cId, uid) =>
  Object.values(ucs).find(uc => uc.course_id === cId && uc.user_id === uid),
);
